import ApiGatewayFunction from "../../UserConfigs/ApiGatewayFunction.js";

/**
 * Creates a brand server-side and returns the real, permanent brand
 * object the backend assigned (id, slug, etc.) - the id is no longer
 * generated client-side (that was the original crypto.randomUUID()
 * placeholder, explicitly flagged at the time as "replace with the id
 * the API returns, once creation is ever pushed" - this is that change).
 *
 * Genuinely needs to read the API's response, unlike the update
 * functions below - the caller can't know the server-assigned id/slug
 * in advance, and needs it immediately after to navigate to the new
 * brand's own page.
 */
export async function createBrand(config, { name, websiteUrl }, setIsSaving, setApiResponse) {
  const payload = {
    request_type: "studio/brand_save",
    request_sub_type: "create_brand",
    client_id: config.clientId,
    name,
    website_url: websiteUrl,
  };

  let result = null;
  await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsSaving,
    (response) => {
      result = response;
      setApiResponse?.(response);
    },
    config.testingState
  );

  const data = result.data;
  if (!data?.ok) {
    throw new Error(data?.error || "Brand creation failed");
  }
  return { brand: data.brand, entry: data.entry };
}

/**
 * Deletes a brand entry server-side. Deliberately does NOT remove the
 * brand's underlying S3 reference files (logo/product/font uploads
 * etc.) - only its entry in the brand list. See the backend's own
 * comment on this for why that's a scope decision, not an oversight.
 */
export async function deleteBrand(config, brandId, setIsSaving, setApiResponse) {
  const payload = {
    request_type: "studio/brand_save",
    request_sub_type: "delete_brand",
    client_id: config.clientId,
    brand_id: brandId,
  };

  let result = null;
  await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsSaving,
    (response) => {
      result = response;
      setApiResponse?.(response);
    },
    config.testingState
  );

  if (!result?.data?.ok) {
    throw new Error(result?.data?.error || "Failed to delete brand");
  }
}

/**
 * Shared by the three update functions below - sends only the one
 * brand's one changed field, never the full brandConfig array. This is
 * the actual fix for the race condition that used to exist here: the
 * old contract sent the client's entire, possibly-stale copy of every
 * brand and blindly overwrote the server's copy with it, so a save from
 * an out-of-date tab could silently erase brands created elsewhere in
 * the meantime. The new backend applies this patch against its own,
 * freshly-read copy inside a lock - this call never needs to know or
 * send anything about any brand other than the one being changed.
 *
 * Deliberately does NOT read the API response - the caller already
 * knows the exact value it just sent (identity/references/offers), so
 * local state is updated with that same value directly rather than
 * needing to parse anything back. Only brand creation (above) actually
 * needs the server's response, since only that operation returns
 * something the client couldn't already know.
 */
async function updateBrandField(config, { brandId, field, value }, setIsSaving, setApiResponse) {
  const payload = {
    request_type: "studio/brand_save",
    request_sub_type: `update_brand_${field}`,
    client_id: config.clientId,
    brand_id: brandId,
    value,
  };

  let result = null;
  await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsSaving,
    (response) => {
      result = response;
      setApiResponse?.(response);
    },
    config.testingState
  );

  if (!result?.data?.ok) {
    throw new Error(result?.data?.error || `Failed to save ${field}`);
  }
}

/**
 * Persists a new credits balance - the top-level (not per-brand)
 * counterpart to updateBrandIdentity/References/Offers above. Needed by
 * handleCreateAsset in App.js, which previously reused the old,
 * full-array-overwrite saveBrandConfig for this same purpose; that
 * function no longer exists, so this replaces it with the same,
 * already-accepted "client computes the exact new value" behavior,
 * just sent as a targeted patch instead of the full array.
 */
export async function updateCredits(config, credits, setIsSaving, setApiResponse) {
  const payload = {
    request_type: "studio/brand_save",
    request_sub_type: "update_credits",
    client_id: config.clientId,
    credits,
  };

  await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsSaving,
    setApiResponse,
    config.testingState
  );
}
export async function updateBrandIdentity(config, { brandId, identity }, setIsSaving, setApiResponse) {
  await updateBrandField(config, { brandId, field: "identity", value: identity }, setIsSaving, setApiResponse);
  return { brandId, identity, status: "active" };
}

/**
 * Persists the references array for an existing brand. `references` is
 * the caller's already-updated array (post-append or post-remove) -
 * this just sends it as the one changed field.
 */
export async function updateBrandReferences(config, { brandId, references }, setIsSaving, setApiResponse) {
  await updateBrandField(config, { brandId, field: "references", value: references }, setIsSaving, setApiResponse);
  return { brandId, references };
}

/**
 * Persists the offers array for an existing brand - same pattern as
 * references. No file involved, so unlike references there's no
 * separate upload step - the caller just computes the next offers
 * array locally and this persists it.
 */
export async function updateBrandOffers(config, { brandId, offers }, setIsSaving, setApiResponse) {
  await updateBrandField(config, { brandId, field: "offers", value: offers }, setIsSaving, setApiResponse);
  return { brandId, offers };
}